import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import ShipmentsPage from './ShipmentsPage';
import { shipment, settings, shipmentSettingsFunc } from '../../../../models/entities/shipment/shipment';
import { RootState } from '../../../../state/store/store';
import { filterFieldsType, gridSortState } from '../../../../state/ducks/allShipmentsPage/allShipmentsPage';
import { allShipmentsPageSelectors } from '../../../../state/ducks/allShipmentsPage/selectors';
import { shipmentsSelectors } from '../../../../state/ducks/shipments/selectors';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { ColumnType } from '../../../shared/Grid/types/Column';
import ExceptionPage from '../../ExceptionPage/ExceptionPage';
import { filterColsByUserSettings } from '../../../shared/Grid/util';
import { userSelectors } from '../../../../state/ducks/user/selectors';
import { userSettings } from '../../../../models/entities/user';
import { singleShipmentSelectors } from '../../../../state/ducks/singleShipment/selectors';
import { claimsDomainsType } from '../../../../userClaims';
import claimsType from '../../../../models/entities/claimsType';
import { featureFlagsModel } from '../../../../models/entities/businessPartner/settings';
import CustomModal from '../../../shared/Modal/Modal';
import followUpSelector from '../../../../state/ducks/followUps/selectors';
import { IFollowUpExtended } from '../../Followup/FollowUp';
import moment from 'moment';
import { MyFollowUps } from '../../Followup/MyFollowUpsModal';

const FOLLOWUP_SNOOZE_KEY = 'followupSnoozeTimestamp';
const SNOOZE_DURATION_MS = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

type Props = {
    shipments: Array<shipment>;
    fetchShipmentsError: any;
    gridColumns: Array<ColumnType<shipment>>;
    currentGridSort: gridSortState | null;
    localizations: any;
    showLoader?: boolean;
    activeShipmentsCount: number;
    shipmentsGridSettings?: userSettings;
    lastVisitShipmentId?: string;
    filterFields: filterFieldsType | null;
    fetchShipments: () => void;
    fetchGridColumns: () => void;
    setGridSort: (sortData: gridSortState) => void;
    settings: shipmentSettingsFunc;
    fetchShipmentsByFilter: (filter: filterFieldsType) => Array<shipment>;
    resetShipmentFilter: () => void;
    getUserClaims: (domain: claimsDomainsType) => claimsType;
    featureFlags?: featureFlagsModel;
    bulkUpdateShipmentState: (payload: { shipmentIds: string[]; state: string }) => Promise<void>;
    bulkUpdateShipmentAssignedTo: (payload: { shipmentIds: string[]; assignedTo: string }) => Promise<void>;
    getFollowUps: () => void;
    followUps: IFollowUpExtended[];
    currentUserId: string;
    users: any[];
};
const ShipmentsPageConnected = ({
    shipments,
    fetchShipmentsError,
    gridColumns,
    currentGridSort,
    localizations,
    showLoader,
    shipmentsGridSettings,
    lastVisitShipmentId,
    filterFields,
    activeShipmentsCount,
    fetchShipments,
    fetchGridColumns,
    setGridSort,
    settings,
    resetShipmentFilter,
    fetchShipmentsByFilter,
    getUserClaims,
    featureFlags,
    bulkUpdateShipmentState,
    bulkUpdateShipmentAssignedTo,
    getFollowUps,
    followUps,
    currentUserId,
    users
}: Props) => {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [hasImportantFollowUps, setHasImportantFollowUps] = useState(false);

    // Check if the modal is snoozed
    const isModalSnoozed = () => {
        const snoozeTimestamp = localStorage.getItem(FOLLOWUP_SNOOZE_KEY);
        if (!snoozeTimestamp) return false;

        const now = new Date().getTime();
        const snoozeTime = parseInt(snoozeTimestamp, 10);

        // Return true if we're still within the snooze period
        return now < snoozeTime;
    };

    // Set a snooze for the modal
    const handleSnoozeModal = () => {
        const snoozeUntil = new Date().getTime() + SNOOZE_DURATION_MS;
        localStorage.setItem(FOLLOWUP_SNOOZE_KEY, snoozeUntil.toString());
        setIsModalVisible(false);
    };

    useEffect(() => {
        if (!filterFields?.find((f: { field: string; value: string }) => f.field === 'isActive')) {
            fetchShipmentsByFilter([]);
        }
        fetchGridColumns();
        // Fetch followups to check for overdue or approaching ones
        getFollowUps();
    }, [fetchShipments, fetchGridColumns, getFollowUps]);

    useEffect(() => {
        if (filterFields?.length) {
            fetchShipmentsByFilter(filterFields);
        }
    }, [filterFields, fetchShipmentsByFilter]);

    // Check if there are any followups that are past due or nearing due date
    useEffect(() => {
        const checkForImportantFollowUps = () => {
            // Find the current user's followups
            const currentUser = users.find((user) => user.id === currentUserId);
            const currentUserFullName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '';

            const userFollowUps = followUps.filter(
                (followUp) => followUp.assignedTo === currentUserId || followUp.assignedTo === currentUserFullName
            );

            // Skip completed tasks
            const activeTasks = userFollowUps.filter((task) => !task.isCompleted);

            // Check for past due or nearing due tasks
            const hasPastDue = activeTasks.some((task) => {
                if (!task.dueDate) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const dueDate = new Date(task.dueDate);
                dueDate.setHours(0, 0, 0, 0);
                return dueDate < today;
            });

            const hasNearingDue = activeTasks.some((task) => {
                if (!task.dueDate) return false;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const dueDate = new Date(task.dueDate);
                dueDate.setHours(0, 0, 0, 0);

                if (dueDate >= today) {
                    const diffTime = Math.abs(dueDate.getTime() - today.getTime());
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays <= 3;
                }
                return false;
            });

            return hasPastDue || hasNearingDue;
        };

        const hasImportant = checkForImportantFollowUps();
        setHasImportantFollowUps(hasImportant);

        // Only show modal if there are important followups and not snoozed
        if (hasImportant && !isModalSnoozed()) {
            const timer = setTimeout(() => {
                setIsModalVisible(true);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [followUps, currentUserId, users]);

    const DEFAULT_SORT_DIRECTION = 'asc';
    const SECOND_SORT_DIRECTION = 'desc';

    const onGridColumnClick = (newColumn: keyof shipment) => {
        if (currentGridSort) {
            const { direction, column } = currentGridSort;
            if (column === newColumn)
                if (direction === DEFAULT_SORT_DIRECTION)
                    setGridSort({
                        column: newColumn,
                        direction: SECOND_SORT_DIRECTION
                    });
                else
                    setGridSort({
                        column: newColumn,
                        direction: DEFAULT_SORT_DIRECTION
                    });
            else {
                setGridSort({
                    column: newColumn,
                    direction: DEFAULT_SORT_DIRECTION
                });
            }
        } else {
            setGridSort({
                column: newColumn,
                direction: DEFAULT_SORT_DIRECTION
            });
        }
    };
    const parsedError = fetchShipmentsError ? JSON.parse(fetchShipmentsError) : null;
    const userSettingKey = 'ShipmentGrid';
    return !fetchShipmentsError ? (
        <>
            <ShipmentsPage
                shipments={shipments}
                bulkUpdateShipmentState={bulkUpdateShipmentState}
                bulkUpdateShipmentAssignedTo={bulkUpdateShipmentAssignedTo}
                fetchShipments={fetchShipments}
                gridColumns={filterColsByUserSettings(gridColumns, shipmentsGridSettings)}
                onGridColumnClick={onGridColumnClick}
                gridSortDirection={currentGridSort?.direction}
                gridSortedBy={currentGridSort?.column}
                localization={localizations}
                showLoader={showLoader}
                activeShipmentsCount={activeShipmentsCount}
                settings={settings}
                userSettingKey={userSettingKey}
                lastVisitShipmentId={lastVisitShipmentId}
                resetFilterFunc={filterFields && filterFields.length > 0 && shipments.length === 0 ? resetShipmentFilter : undefined}
                getUserClaims={getUserClaims}
            />
            {hasImportantFollowUps && (
                <CustomModal
                    isOpen={isModalVisible}
                    width="50%"
                    maxWidth="600px"
                    height={400}
                    title={{ text: 'Task Followup Notification' }}
                    cancelBtn={{ text: 'Close' }}
                    onCancelCallback={() => setIsModalVisible(false)}
                    HeaderContent={() => (
                        <button
                            onClick={handleSnoozeModal}
                            style={{
                                padding: '8px 16px',
                                backgroundColor: '#f0f0f0',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                marginLeft: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                        >
                            <span className="material-icons" style={{ fontSize: '18px' }}>
                                snooze
                            </span>
                            Snooze for 6 hours
                        </button>
                    )}
                    Content={() => <MyFollowUps currentUserId={currentUserId} users={users} followUps={followUps} />}
                />
            )}
        </>
    ) : (
        <ExceptionPage status={parsedError.status} subTitle={parsedError.message} goBackPath="/" />
    );
};

const mapStateToProps = (state: RootState) => ({
    activeShipmentsCount: allShipmentsPageSelectors.activeShipmentsCount(state),
    shipments: allShipmentsPageSelectors.currentGridDataPage(state),
    fetchShipmentsError: shipmentsSelectors.fetchShipmentsError(state),
    gridColumns: allShipmentsPageSelectors.gridColumns(state),
    currentGridSort: allShipmentsPageSelectors.gridSort(state),
    localizations: localizationSelectors.allShipmentsPage(state),
    showLoader:
        state.loading.effects.allShipmentsPage.fetchGridColumns ||
        state.loading.effects.shipments.fetchShipments ||
        state.loading.effects.allShipmentsPage.fetchShipmentsByFilter,
    settings: shipmentsSelectors.settingsByLanguage(state),
    shipmentsGridSettings: userSelectors.shipmentsGridSettings(state),
    lastVisitShipmentId: singleShipmentSelectors.currentShipmentId(state) || undefined,
    filterFields: allShipmentsPageSelectors.filterFields(state),
    getUserClaims: (domain: claimsDomainsType) => userSelectors.getUserClaims(state)(domain),
    featureFlags: userSelectors.featureFlags(state),
    followUps: followUpSelector.filteredFollowUps(state),
    currentUserId: state.user.userInfo.id,
    users: userSelectors.usersInSameNetworkIncludeSelf(state)
});
const mapDispatchToProps = (dispatch: any) => ({
    fetchShipments: () => dispatch.shipments.fetchShipments(),
    fetchGridColumns: () => dispatch.allShipmentsPage.fetchGridColumns(),
    setGridSort: (sortData: gridSortState) => dispatch.allShipmentsPage.setGridSort(sortData),
    resetShipmentFilter: () => dispatch.allShipmentsPage.setShipmentFilters({ filter: [] }),
    fetchShipmentsByFilter: (filter: filterFieldsType) => dispatch.allShipmentsPage.fetchShipmentsByFilter(filter),
    bulkUpdateShipmentState: (payload: { shipmentIds: string[]; state: string }) => dispatch.allShipmentsPage.bulkUpdateShipmentState(payload),
    bulkUpdateShipmentAssignedTo: (payload: { shipmentIds: string[]; assignedTo: string }) => dispatch.allShipmentsPage.bulkUpdateShipmentAssignedTo(payload),
    getFollowUps: () => dispatch.followUps.getFollowUps()
});

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentsPageConnected);